import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Throwing } from '../models/common';
import { webApiGet, webApiPost, webApiDelete } from '../api/client';
import { UpdateAccountInfoData } from './apiModels';
import { AppThunk } from '../store/store';
import { Restaurant } from '../api/models/RestaurantData';
import { Supplier } from '../api/models/QuotedData';
import { PaymentCard } from '../api/models/OrderData';


export interface AccountDetailsState extends Throwing {
    restaurantDetails: Restaurant | null,
    supplierDetails: Supplier | null,
    loadUserDetailsBusy: boolean,
    loadUserDetailsSuccess: boolean | null,

    updateAccountInfoBusy: boolean,
    updateAccountInfoSuccess: boolean | null,

    getPaymentCardsBusy: boolean,
    paymentCards: PaymentCard[] | null

    removePaymentCardDataBusy: boolean,
    removePaymentCardDataSuccess: boolean | null
}

let initialState: AccountDetailsState = {
    restaurantDetails: null,
    supplierDetails: null,
    loadUserDetailsBusy: false,
    loadUserDetailsSuccess: null,

    updateAccountInfoBusy: false,
    updateAccountInfoSuccess: null,

    getPaymentCardsBusy: false,
    paymentCards: null,

    removePaymentCardDataBusy: false,
    removePaymentCardDataSuccess: null,
}

export const accountDetailsSlice = createSlice({
    name: 'accountDetails',
    initialState,
    reducers: {

        getRestaurantInfoRequest: (state: AccountDetailsState) => { return { ...state, loadUserDetailsBusy: true, loadUserDetailsSuccess: null } },
        getRestaurantInfoResponse: (state: AccountDetailsState, action) => {
            return {
                ...state,
                loadUserDetailsBusy: false,
                loadUserDetailsSuccess: true,
                restaurantDetails: action.payload
            }
        },
        getRestaurantInfoError: (state: AccountDetailsState, action) => { return { ...state, error: action.payload, loginErrorMessage: action.payload.message, loadUserDetailsBusy: false, loadUserDetailsSuccess: false } },


        getSupplierInfoRequest: (state: AccountDetailsState) => { return { ...state, loadUserDetailsBusy: true, loadUserDetailsSuccess: null } },
        getSupplierInfoResponse: (state: AccountDetailsState, action) => {
            return { ...state, loadUserDetailsBusy: false, loadUserDetailsSuccess: true, supplierDetails: action.payload }
        },
        getSupplierInfoError: (state: AccountDetailsState, action) => { return { ...state, error: action.payload, loginErrorMessage: action.payload.message, loadUserDetailsBusy: false, loadUserDetailsSuccess: false } },


        setAccountInfoRequest: (state: AccountDetailsState) => { return { ...state, updateAccountInfoBusy: true, updateAccountInfoSuccess: null } },
        setAccountInfoResponse: (state: AccountDetailsState, action) => {
            return { ...state, updateAccountInfoBusy: false, updateAccountInfoSuccess: true, supplierDetails: action.payload.supplierData || null, restaurantDetails: action.payload.restaurantData || null }
        },
        setAccountInfoError: (state: AccountDetailsState, action) => { return { ...state, error: action.payload, loginErrorMessage: action.payload.message, updateAccountInfoBusy: false, updateAccountInfoSuccess: false } },


        getRestaurantPaymentCardsRequest: (state: AccountDetailsState) => {
            state.getPaymentCardsBusy = true
            state.paymentCards = null
        },
        getRestaurantPaymentCardsResponse: (state: AccountDetailsState, action) => {
            state.getPaymentCardsBusy = false
            state.paymentCards = action.payload
        },
        getRestaurantPaymentCardsError: (state: AccountDetailsState, action) => {
            state.getPaymentCardsBusy = false
            state.error = action.payload
        },


        removeRestaurantPaymentCardRequest: (state: AccountDetailsState) => {
            state.removePaymentCardDataBusy = true
            state.removePaymentCardDataSuccess = null
        },
        removeRestaurantPaymentCardResponse: (state: AccountDetailsState) => {
            state.removePaymentCardDataBusy = false
            state.removePaymentCardDataSuccess = true
        },
        removeRestaurantPaymentCardError: (state: AccountDetailsState, action) => {
            state.removePaymentCardDataBusy = false
            state.removePaymentCardDataSuccess = false
            state.error = action.payload
        },
    }
});


export const getSupplierInfoAsyncThunk = (): AppThunk => dispatch => {
    dispatch(accountDetailsSlice.actions.getSupplierInfoRequest())

    webApiGet(
        "/auth/info/supplier",
        (data: Supplier) => {
            dispatch(accountDetailsSlice.actions.getSupplierInfoResponse(data));
        },
        (err: Error) => { dispatch(accountDetailsSlice.actions.getSupplierInfoError(err)); },
        {},
        3
    )
}

export const getRestaurantInfoAsyncThunk = (): AppThunk => dispatch => {
    dispatch(accountDetailsSlice.actions.getRestaurantInfoRequest())

    webApiGet(
        "/auth/info/restaurant",
        (data: Restaurant) => {
            dispatch(accountDetailsSlice.actions.getRestaurantInfoResponse(data));
        },
        (err: Error) => { dispatch(accountDetailsSlice.actions.getRestaurantInfoError(err)); },
        {},
        3
    )
}

export const setAccountInfoAsyncThunk = (updateData: UpdateAccountInfoData): AppThunk => dispatch => {
    dispatch(accountDetailsSlice.actions.setAccountInfoRequest())

    webApiPost(
        "/auth/info/",
        Object.assign({ supplierData: null, restaurantData: null }, updateData),
        (updatedData: UpdateAccountInfoData) => {
            dispatch(accountDetailsSlice.actions.setAccountInfoResponse(updatedData));
        },
        (err: Error) => { dispatch(accountDetailsSlice.actions.setAccountInfoError(err)); },
    )
}


export const getRestaurantPaymentCardsAsyncThunk = (): AppThunk => dispatch => {
    dispatch(accountDetailsSlice.actions.getRestaurantPaymentCardsRequest())

    webApiGet(
        "/restaurant/payments/card",
        (data: Restaurant) => {
            dispatch(accountDetailsSlice.actions.getRestaurantPaymentCardsResponse(data));
        },
        (err: Error) => { dispatch(accountDetailsSlice.actions.getRestaurantPaymentCardsError(err)); },
        {},
        3
    )
}


export const removePaymentCardDataAsyncThunk = (cardId: string): AppThunk => dispatch => {
    dispatch(accountDetailsSlice.actions.removeRestaurantPaymentCardRequest())

    webApiDelete(
        "/restaurant/payments/card",
        { cardId },
        (data: any) => {
            dispatch(accountDetailsSlice.actions.removeRestaurantPaymentCardResponse());
        },
        (err: Error) => { dispatch(accountDetailsSlice.actions.removeRestaurantPaymentCardError(err)); },
    )
}



export default accountDetailsSlice.reducer;